import React, { useCallback, useEffect, useRef, useState } from "react";
import { doc, getDoc } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { httpsCallable } from "firebase/functions";
import toast from "react-hot-toast";
import { ChevronDown, ChevronUp, GripVertical, Trash2, Upload } from "lucide-react";
import { db, functions, storage } from "../firebase";
import { compressImage, formatBytes } from "../utils/compress-image";
// Same file the bake script reads, so the picker can never show an icon the
// live page doesn't have.
import BONUS_ICONS from "../utils/bonus-icons.json";
// Same rule the bake script applies, so the preview below and the live page
// agree on whether a value is money or a label.
import {
  parseBonusValue,
  formatBonusMoney,
  totalBonusValue,
} from "../utils/bonus-value";
// Strikethrough lives in the title as `~~markdown~~` — the checkbox below is a
// shortcut for typing the tildes, not a second place the state is kept.
import {
  bonusTitleText,
  canStrikeBonus,
  isBonusStruck,
  setBonusTitleStruck,
} from "../utils/bonus-markdown";

/* Editor for the offer shown on the checkout pages.
 *
 * Publishing writes Firestore and triggers a Netlify rebuild, which bakes the
 * new values into the static page — so expect ~2 minutes before the live page
 * reflects a change. The price charged, however, is read from Firestore on
 * every order, so it takes effect immediately regardless of the rebuild.
 *
 * The webinar runs over two days with a different offer on each. Each day is a
 * separate doc and a separate page; they share nothing, so publishing day 2
 * mid-webinar cannot move day 1's price under a buyer who is mid-checkout.
 * The cost of that independence is that a change meant for both days has to be
 * entered and published twice. */

/* Exported because the page renders the day switch in its header tab row and
 * needs the same labels — one list, so a third day never has to be added in
 * two places. */
export const DAYS = [
  { day: 1, label: "Day 1", doc: "site_config/vip_checkout", url: "/checkout/" },
  { day: 2, label: "Day 2", doc: "site_config/vip_checkout_day2", url: "/checkout-day2/" },
];

const ICON_KEYS = Object.keys(BONUS_ICONS);

/* Renders one icon from the shared set. The markup is a developer-authored
   constant in bonus-icons.json, never user input, so injecting it is safe —
   and it keeps the admin preview pixel-identical to what gets baked. */
function BonusIcon({ name, size = 18 }) {
  const paths = BONUS_ICONS[name];
  if (!paths) return null;
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      dangerouslySetInnerHTML={{ __html: paths }}
    />
  );
}

/* Click the swatch, pick from a grid of the actual icons. A name-only dropdown
   made you guess what "blueprint" looked like and then publish to find out. */
function IconPicker({ value, onChange }) {
  return (
    <div className="dropdown">
      <button
        tabIndex={0}
        type="button"
        className="btn btn-sm btn-outline w-14"
        aria-label={`Icon: ${value || "none"}`}
      >
        {value ? <BonusIcon name={value} /> : <span className="opacity-40">—</span>}
      </button>
      {/* Explicit width: dropdown-content is absolutely positioned and shrinks
          to fit, which collapses the grid into a single narrow column. */}
      <div
        tabIndex={0}
        className="dropdown-content z-[1] mt-1 w-56 p-2 bg-base-100 rounded-box shadow-lg border border-base-300"
      >
        <div className="grid grid-cols-5 gap-1">
          <button
            type="button"
            className={`flex h-9 w-9 items-center justify-center rounded hover:bg-base-200 ${
              !value ? "bg-primary/15 text-primary" : ""
            }`}
            onClick={() => onChange("")}
            title="No icon"
          >
            <span className="opacity-40">—</span>
          </button>
          {ICON_KEYS.map((key) => (
            <button
              key={key}
              type="button"
              className={`flex h-9 w-9 items-center justify-center rounded hover:bg-base-200 ${
                value === key ? "bg-primary/15 text-primary" : ""
              }`}
              onClick={() => onChange(key)}
              title={key}
            >
              <BonusIcon name={key} size={20} />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const senToRM = (sen) => (Number.isInteger(sen) ? (sen / 100).toFixed(2) : "");
const rmToSen = (rm) => Math.round(parseFloat(rm) * 100);

/* Bonus rows are reorderable, so React needs a key that travels with the row
   rather than one that describes where it currently sits: with index keys a
   move re-labels the existing DOM nodes instead of moving them, which leaves
   focus on the arrow button of whatever row slid into that slot. The id is
   local to the form — handlePublish writes the stored fields out by name, so
   it never reaches Firestore. */
let bonusUid = 0;
const nextBonusId = () => `b${++bonusUid}`;

const moveItem = (list, from, to) => {
  const next = list.slice();
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
};

/* Two short fields per line on a phone: half the width, less half the gap-2
   between them. */
const HALF_ON_MOBILE = "w-[calc(50%-0.25rem)]";

/* One field inside a repeated row. On a laptop the row reads as a single line
   and a field's position is what says which one it is — so the label is
   hidden. Stacked on a phone that cue is gone, and an unlabelled column of
   seven boxes is unfillable, so the label shows. The inputs keep their
   aria-label either way; this is a visual cue, not the accessible name. */
function RowField({ label, className = "", children }) {
  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <span className="text-[10px] uppercase tracking-wide opacity-50 sm:hidden">
        {label}
      </span>
      {children}
    </div>
  );
}

const EMPTY = {
  title: "",
  titleZh: "",
  amount: "",
  images: [],
  bonuses: [],
  // Not editable any more — every value renders with a "$" and the total uses
  // the same helper as the rows. Still stored so the doc shape is unchanged and
  // the server's validator keeps seeing the field it expects.
  bonusCurrency: "USD",
  promoCodes: [],
  seatsStart: "",
  seatsFloor: "3",
};

/* Firestore doc -> form state. Shared by the initial load and by "Copy from
   Day 1", so a copied offer is normalised exactly like a loaded one. */
function toForm(d) {
  return {
    title: d.title || "",
    titleZh: d.titleZh || "",
    amount: senToRM(d.amount),
    // Older configs stored bare URL strings; normalise so the rest of
    // the component only deals with one shape.
    images: (Array.isArray(d.images) ? d.images : []).map((img) =>
      typeof img === "string" ? { url: img, width: null, height: null } : img
    ),
    // Older configs stored bonuses as plain name strings.
    bonuses: (Array.isArray(d.bonuses) ? d.bonuses : []).map((b) =>
      typeof b === "string"
        ? {
            _id: nextBonusId(),
            icon: "",
            name: b,
            nameZh: "",
            tag: "",
            tagZh: "",
            value: "",
            valueZh: "",
          }
        : {
            _id: nextBonusId(),
            icon: b.icon || "",
            name: b.name || "",
            nameZh: b.nameZh || "",
            tag: b.tag || "",
            tagZh: b.tagZh || "",
            // Stored as a number or a label; the field edits both as text.
            value: b.value == null ? "" : String(b.value),
            valueZh: b.valueZh || "",
          }
    ),
    bonusCurrency: d.bonusCurrency || "USD",
    promoCodes: Object.entries(d.promoCodes || {}).map(([code, p]) => ({
      code,
      type: p.type || "flat",
      value: p.type === "percent" ? String(p.value) : senToRM(p.value),
      expires: p.expires ? String(p.expires).slice(0, 10) : "",
    })),
    seatsStart: d.seats?.start ? String(d.seats.start) : "",
    seatsFloor: d.seats?.floor != null ? String(d.seats.floor) : "3",
  };
}

/* `day` is owned by the page so its switch can sit in the header tab row next
 * to Orders / Edit offer, and Publish lives up there too. This component reads
 * the day, hands its publish action up through `publishRef`, and reports its
 * own status so those header controls can disable themselves — they are no
 * longer close enough to see a publish in flight for themselves. */
export default function CheckoutEditor({ token, day = 1, onStatusChange, publishRef }) {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [uploading, setUploading] = useState(false);
  // Drag state for the bonus list. dragIndex is the row being carried,
  // overIndex the row it would land on — the list itself is left alone until
  // the drop, so the row under the cursor never slides out from under it.
  const [dragIndex, setDragIndex] = useState(null);
  const [overIndex, setOverIndex] = useState(null);
  // Rows are only draggable while the grip is held. Making the whole row
  // draggable turns selecting text in any of its inputs into a drag.
  const [dragArmed, setDragArmed] = useState(null);
  const fileInput = useRef(null);

  const active = DAYS.find((d) => d.day === day) || DAYS[0];

  const set = useCallback((patch) => setForm((f) => ({ ...f, ...patch })), []);

  const moveBonus = useCallback((from, to) => {
    setForm((f) => {
      if (from === to || to < 0 || to >= f.bonuses.length) return f;
      return { ...f, bonuses: moveItem(f.bonuses, from, to) };
    });
  }, []);

  const endBonusDrag = useCallback(() => {
    setDragIndex(null);
    setOverIndex(null);
    setDragArmed(null);
  }, []);

  // Keeps the page's day switch and Publish button honest: the switch locks
  // while a publish is in flight so a stray click cannot swap the form out from
  // under the request, and Publish also waits out the initial load.
  useEffect(() => {
    if (onStatusChange) onStatusChange({ publishing, loading });
  }, [publishing, loading, onStatusChange]);

  // Reloads on every day switch. Deliberately drops unpublished edits rather
  // than carrying them across: silently moving half-typed day 1 copy onto
  // day 2's form is how the wrong price gets published.
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const snap = await getDoc(doc(db, active.doc));
        if (cancelled) return;
        // A day that has never been published has no doc yet — an empty form
        // is the honest representation of that, not an error.
        setForm(snap.exists() ? toForm(snap.data()) : EMPTY);
      } catch (e) {
        if (!cancelled) toast.error(`Could not load the ${active.label} checkout config.`);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [active.doc, active.label]);


  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const result = await compressImage(file);
      const ext = result.type === "image/webp" ? "webp" : "jpg";
      const path = `checkout/${Date.now()}-hero.${ext}`;
      const storageRef = ref(storage, path);
      await uploadBytes(storageRef, result.blob, { contentType: result.type });
      const url = await getDownloadURL(storageRef);

      // Dimensions travel with the URL so the checkout page can reserve the
      // right space before the image loads — the hero keeps its own shape, so
      // without them the price jumps down as it arrives.
      set({
        images: [
          { url, width: result.width, height: result.height },
          ...form.images,
        ].slice(0, 6),
      });
      toast.success(
        result.compressed
          ? `Uploaded — ${formatBytes(result.originalBytes)} compressed to ${formatBytes(result.bytes)}`
          : `Uploaded (${formatBytes(result.bytes)}, already optimised)`
      );
    } catch (err) {
      toast.error(err.message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileInput.current) fileInput.current.value = "";
    }
  };

  const handlePublish = async () => {
    const amount = rmToSen(form.amount);

    if (!form.title.trim()) return toast.error("Give the product a title.");
    if (!Number.isInteger(amount)) {
      return toast.error("Enter the price in ringgit, e.g. 5998.00");
    }

    // A flat code worth more than the price would charge the floor amount,
    // which is never what anyone meant.
    const tooBig = form.promoCodes.find(
      (p) => p.code.trim() && p.type === "flat" && rmToSen(p.value) >= amount
    );
    if (tooBig) {
      return toast.error(
        `Code ${tooBig.code.trim().toUpperCase()} is worth more than the price.`
      );
    }

    const promoCodes = {};
    for (const p of form.promoCodes) {
      if (!p.code.trim()) continue;
      promoCodes[p.code.trim().toUpperCase()] = {
        type: p.type,
        value: p.type === "percent" ? Number(p.value) : rmToSen(p.value),
        expires: p.expires || null,
      };
    }

    const config = {
      title: form.title.trim(),
      titleZh: form.titleZh.trim(),
      amount,
      images: form.images,
      bonuses: form.bonuses
        .filter((b) => b.name.trim())
        .map((b) => ({
          icon: b.icon.trim(),
          // Titles publish with their strike markers normalised: both languages
          // carry them or neither does, and there's no padding left inside the
          // tildes. The page strikes the whole row off either title, so storing
          // a half-marked pair would leave the config saying something the
          // rendered page doesn't.
          name: setBonusTitleStruck(b.name, isBonusStruck(b)) || b.name.trim(),
          nameZh: setBonusTitleStruck(b.nameZh, isBonusStruck(b)),
          tag: b.tag.trim(),
          tagZh: (b.tagZh || "").trim(),
          // Sent as typed. Deciding number-vs-label here as well as on the
          // server would be two places to disagree about what a buyer sees;
          // publishCheckoutConfig parses it and its answer is the one stored.
          value: String(b.value ?? "").trim(),
          valueZh: (b.valueZh || "").trim(),
        })),
      bonusCurrency: form.bonusCurrency.trim() || "USD",
      promoCodes,
      seats: form.seatsStart
        ? { start: Number(form.seatsStart), floor: Number(form.seatsFloor || 3) }
        : null,
    };

    setPublishing(true);
    try {
      const publish = httpsCallable(functions, "publishCheckoutConfig");
      const res = await publish({ token, day, config });
      const data = res.data || {};

      if (!data.ok) {
        toast.error(`Rejected: ${data.error || "unknown error"}`);
      } else if (!data.rebuilt) {
        // Saved but the site didn't rebuild — the price is already live for new
        // orders, but the page itself still shows the old copy.
        toast(
          "Saved, but the site rebuild didn't trigger. Prices apply now; the page will update on the next deploy.",
          { duration: 8000, icon: "⚠️" }
        );
      } else {
        toast.success(`${active.label} published — live at ${active.url} in ~2 minutes.`);
      }
    } catch (err) {
      toast.error(err.message || "Publish failed.");
    } finally {
      setPublishing(false);
    }
  };

  /* Hands the publish action to the page's header button. Reassigned on every
     render, deliberately without a dependency array, because handlePublish
     closes over the current form — a stale copy would publish whatever was
     typed one render ago. */
  useEffect(() => {
    if (!publishRef) return undefined;
    publishRef.current = handlePublish;
    return () => {
      publishRef.current = null;
    };
  });

  if (loading) {
    return (
      <div className="card bg-base-100 shadow">
        <div className="card-body gap-4 p-4 sm:p-8">
          <div className="flex items-center justify-center p-10">
            <span className="loading loading-spinner" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="card bg-base-100 shadow">
      <div className="card-body gap-4 p-4 sm:p-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="card-title">Checkout Page — {active.label}</h2>
            <p className="text-sm opacity-70">
              Edits go live on <code>{active.url}</code> about 2 minutes after publishing.
              Prices apply to new orders immediately. Each day is a separate
              offer — publishing this one leaves the other untouched.
            </p>
          </div>
          {/* Actions all live in the page header tab row now. */}
        </div>

        {/* ---- Product ---- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Product title</span>
              <span className="label-text-alt opacity-60">English</span>
            </label>
            <input
              className="input input-bordered"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="JS VIP Community"
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">产品名称</span>
              <span className="label-text-alt opacity-60">
                blank = use English
              </span>
            </label>
            <input
              className="input input-bordered"
              value={form.titleZh}
              onChange={(e) => set({ titleZh: e.target.value })}
              placeholder="JS VIP 培训计划"
            />
          </div>
        </div>

        <div className="form-control">
          <label className="label">
            <span className="label-text">Price (RM)</span>
            <span className="label-text-alt opacity-60">
              charged when no promo code is used
            </span>
          </label>
          <input
            className="input input-bordered"
            inputMode="decimal"
            value={form.amount}
            onChange={(e) => set({ amount: e.target.value })}
            placeholder="5998.00"
          />
          <p className="text-xs opacity-60 mt-1">
            Promo codes discount from this. The struck-through price and “you
            save” badge only appear once a buyer applies a code, so the crossed
            out figure is always what they’d otherwise have paid.
          </p>
        </div>

        {/* ---- Images ---- */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Hero image</span>
            <span className="label-text-alt opacity-60">
              1920×1080 — compressed automatically
            </span>
          </label>
          <input
            ref={fileInput}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleUpload}
          />
          <button
            className="btn btn-outline"
            onClick={() => fileInput.current?.click()}
            disabled={uploading}
          >
            {uploading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <Upload size={16} />
            )}
            Upload image
          </button>

          {form.images.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-3">
              {form.images.map((img, i) => (
                <div key={img.url} className="relative">
                  <img
                    src={img.url}
                    alt=""
                    className={`rounded w-full ${
                      i === 0 ? "ring-2 ring-primary" : ""
                    }`}
                  />
                  {i === 0 && (
                    <span className="badge badge-primary badge-sm absolute top-1 left-1">
                      Live
                    </span>
                  )}
                  {img.width && (
                    <span className="badge badge-neutral badge-sm absolute bottom-1 left-1">
                      {img.width}×{img.height}
                    </span>
                  )}
                  <button
                    className="btn btn-sm sm:btn-xs btn-circle absolute top-1 right-1"
                    onClick={() =>
                      set({
                        images: form.images.filter((x) => x.url !== img.url),
                      })
                    }
                    aria-label="Remove image"
                  >
                    <Trash2 size={12} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ---- Bonuses ---- */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Bonuses</span>
            <span className="label-text-alt opacity-60">
              gold stack under the price
            </span>
          </label>
          <div className="space-y-2">
            {form.bonuses.map((b, i) => (
              <div
                key={b._id || i}
                draggable={dragArmed === i}
                onDragStart={(e) => {
                  setDragIndex(i);
                  e.dataTransfer.effectAllowed = "move";
                  // Firefox starts no drag at all unless some data is set.
                  e.dataTransfer.setData("text/plain", String(i));
                }}
                onDragOver={(e) => {
                  if (dragIndex === null) return;
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setOverIndex(i);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  if (dragIndex !== null) moveBonus(dragIndex, i);
                  endBonusDrag();
                }}
                onDragEnd={endBonusDrag}
                // Transparent borders on every row so the drop line doesn't
                // shift the list by 2px as it appears. The tint is what makes
                // one bonus readable as one block once the fields stack.
                className={`rounded-lg border-y-2 border-transparent bg-base-200/60 p-2 sm:rounded-none sm:bg-transparent sm:p-0 ${
                  dragIndex === i ? "opacity-40" : ""
                } ${
                  dragIndex !== null && overIndex === i && dragIndex !== i
                    ? dragIndex > i
                      ? "!border-t-primary"
                      : "!border-b-primary"
                    : ""
                }`}
              >
                {/* Phone only: names the row and carries Remove, so every
                    field below is free to take the full width. */}
                <div className="mb-2 flex items-center justify-between sm:hidden">
                  <span className="text-xs font-semibold opacity-60">
                    Bonus {i + 1}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1 text-error"
                    onClick={() =>
                      set({ bonuses: form.bonuses.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>

                <div className="flex gap-2 flex-wrap items-end">
                  <RowField label="Reorder">
                  <div className="flex items-center shrink-0">
                    {/* Drag needs a mouse, so the grip is desktop-only; the
                        arrows are the same move for touch and for keyboard,
                        where no drag event ever fires. */}
                    <span
                      className="hidden sm:inline-flex cursor-grab active:cursor-grabbing px-0.5 opacity-40 hover:opacity-90"
                      onMouseDown={() => setDragArmed(i)}
                      onMouseUp={() => setDragArmed(null)}
                      title="Drag to reorder"
                      aria-hidden="true"
                    >
                      <GripVertical size={14} />
                    </span>
                    {/* Side by side and thumb-sized on a phone; stacked and
                        compact once they sit inside a one-line row. */}
                    <div className="flex flex-row gap-1 sm:flex-col sm:gap-0">
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs h-9 min-h-0 px-3 sm:h-4 sm:px-1"
                        onClick={() => moveBonus(i, i - 1)}
                        disabled={i === 0}
                        aria-label={`Move bonus ${i + 1} up`}
                        title="Move up"
                      >
                        <ChevronUp size={12} />
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost btn-xs h-9 min-h-0 px-3 sm:h-4 sm:px-1"
                        onClick={() => moveBonus(i, i + 1)}
                        disabled={i === form.bonuses.length - 1}
                        aria-label={`Move bonus ${i + 1} down`}
                        title="Move down"
                      >
                        <ChevronDown size={12} />
                      </button>
                    </div>
                  </div>
                  </RowField>
                  <RowField label="Icon">
                    <IconPicker
                      value={b.icon}
                      onChange={(icon) => updateAt(form, set, "bonuses", i, { icon })}
                    />
                  </RowField>
                  <RowField label="Name (EN)" className="w-full sm:flex-1 sm:min-w-[10rem]">
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.name}
                      onChange={(e) => updateAt(form, set, "bonuses", i, { name: e.target.value })}
                      placeholder="Jason's daily market analysis"
                      aria-label="Bonus name (English)"
                    />
                  </RowField>
                  <RowField label="名称 (中文)" className="w-full sm:flex-1 sm:min-w-[10rem]">
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.nameZh}
                      onChange={(e) => updateAt(form, set, "bonuses", i, { nameZh: e.target.value })}
                      placeholder="Jason 每日市场分析"
                      aria-label="Bonus name (Chinese)"
                    />
                  </RowField>
                  <RowField label="Tag (EN)" className={HALF_ON_MOBILE + " sm:w-28"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.tag}
                      onChange={(e) => updateAt(form, set, "bonuses", i, { tag: e.target.value })}
                      placeholder="LIMITED"
                      aria-label="Tag (English)"
                    />
                  </RowField>
                  <RowField label="标签 (中文)" className={HALF_ON_MOBILE + " sm:w-28"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      value={b.tagZh}
                      onChange={(e) => updateAt(form, set, "bonuses", i, { tagZh: e.target.value })}
                      placeholder="限量"
                      aria-label="Tag (Chinese)"
                    />
                  </RowField>
                  {/* Number or label. The Chinese box appears only once what's
                      typed stops being a number — there is nothing to translate
                      about "597", and an always-visible field would suggest
                      otherwise. */}
                  <RowField label="Value" className={HALF_ON_MOBILE + " sm:w-24"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      inputMode="decimal"
                      value={b.value}
                      onChange={(e) => updateAt(form, set, "bonuses", i, { value: e.target.value })}
                      placeholder="597 or Priceless"
                      aria-label="Value — a number, or a text label shown as-is"
                    />
                  </RowField>
                  {parseBonusValue(b.value).kind === "label" && (
                    <RowField label="价值 (中文)" className={HALF_ON_MOBILE + " sm:w-24"}>
                      <input
                        className="input input-bordered input-sm w-full"
                        value={b.valueZh || ""}
                        onChange={(e) =>
                          updateAt(form, set, "bonuses", i, { valueZh: e.target.value })
                        }
                        placeholder="无价"
                        aria-label="Value label (Chinese)"
                      />
                    </RowField>
                  )}
                  {/* Sits last before the bin: strike and delete are the two
                      "retire this bonus" actions, so they read together at the
                      end of the row rather than the checkbox interrupting the
                      run of text fields.
                      Writes the ~~tildes~~ into both titles at once, so the
                      state can't end up set in one language and not the other.
                      Typing them by hand does the same thing — the checkbox
                      just reflects the field. */}
                  <RowField label="Struck out">
                    <label
                      className="flex h-8 cursor-pointer items-center gap-2 whitespace-nowrap px-1"
                      title={
                        !b.name.trim()
                          ? "Give the bonus a name first"
                          : !canStrikeBonus(b)
                          ? "This name already contains ~~, so the marker can't be read back off it. Remove the tildes to use the checkbox."
                          : "Cross this bonus out — it stays on the page, greyed and struck through, and drops out of the bonus total"
                      }
                    >
                      <input
                        type="checkbox"
                        className="checkbox checkbox-sm"
                        checked={isBonusStruck(b)}
                        disabled={!b.name.trim() || !canStrikeBonus(b)}
                        onChange={(e) =>
                          updateAt(form, set, "bonuses", i, {
                            name: setBonusTitleStruck(b.name, e.target.checked),
                            nameZh: setBonusTitleStruck(b.nameZh, e.target.checked),
                          })
                        }
                        aria-label="Cross this bonus out"
                      />
                      <span className="hidden text-xs opacity-60 sm:inline">
                        <s>Strike</s>
                      </span>
                    </label>
                  </RowField>
                  <button
                    type="button"
                    className="hidden sm:inline-flex btn btn-xs btn-ghost"
                    onClick={() =>
                      set({ bonuses: form.bonuses.filter((_, j) => j !== i) })
                    }
                    aria-label="Remove bonus"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 items-center flex-wrap mt-2">
            <button
              className="btn btn-sm btn-ghost"
              onClick={() =>
                set({
                  bonuses: [
                    ...form.bonuses,
                    {
                      _id: nextBonusId(),
                      icon: "",
                      name: "",
                      nameZh: "",
                      tag: "",
                      tagZh: "",
                      value: "",
                      valueZh: "",
                    },
                  ],
                })
              }
            >
              + Add bonus
            </button>
          </div>
          <p className="text-xs opacity-60 mt-1">
            Each row is: icon · name (EN) · name (中文) · tag (EN) · tag (中文) ·
            value · strike. Drag a row by its grip — or use the arrows — to reorder;
            rows publish in the order shown here.
            Leave a Chinese field blank and the English shows instead.
            Tags like “LIMITED” or “FIRST 20” are promises the page doesn’t
            enforce — only use them if they’re genuinely true.
          </p>
          <p className="text-xs opacity-60">
            <b>Value</b> takes a number or a word. A number shows as a price
            (“597” → <b>$597</b>) and counts toward the total; anything else
            shows exactly as typed with no dollar sign (“Priceless”) and is left
            out of the total. Type a label and a 中文 box appears next to it.
          </p>
          <p className="text-xs opacity-60">
            <b>Strike</b> crosses a bonus out when it’s no longer on offer. The
            row stays on the page — greyed out and struck through, so nobody
            wonders where it went — but it stops counting toward the bonus total
            and toward the “Bonuses (n items)” line in the order summary. It’s
            stored as markdown in the name (
            <code className="px-1">~~Free coaching call~~</code>), so typing the
            tildes yourself does the same thing.
          </p>

          {form.bonuses.some((b) => b.name.trim()) && (
            <BonusPreview bonuses={form.bonuses} />
          )}
        </div>

        {/* ---- Promo codes ---- */}
        <div className="form-control">
          <label className="label">
            <span className="label-text">Promo codes</span>
            <span className="label-text-alt opacity-60">
              discounts off the selling price
            </span>
          </label>
          <div className="space-y-2">
            {form.promoCodes.map((p, i) => (
              <div
                key={i}
                className="rounded-lg bg-base-200/60 p-2 sm:rounded-none sm:bg-transparent sm:p-0"
              >
                <div className="mb-2 flex items-center justify-between sm:hidden">
                  <span className="text-xs font-semibold opacity-60">
                    {p.code.trim() ? p.code.trim().toUpperCase() : `Code ${i + 1}`}
                  </span>
                  <button
                    type="button"
                    className="btn btn-ghost btn-xs gap-1 text-error"
                    onClick={() =>
                      set({ promoCodes: form.promoCodes.filter((_, j) => j !== i) })
                    }
                  >
                    <Trash2 size={14} />
                    Remove
                  </button>
                </div>

                <div className="flex gap-2 flex-wrap items-end">
                  <RowField label="Code" className="w-full sm:w-32">
                    <input
                      className="input input-bordered input-sm w-full uppercase"
                      value={p.code}
                      onChange={(e) => updateAt(form, set, "promoCodes", i, { code: e.target.value })}
                      placeholder="CODE"
                      aria-label="Promo code"
                    />
                  </RowField>
                  <RowField label="Discount" className={HALF_ON_MOBILE + " sm:w-auto"}>
                    <select
                      className="select select-bordered select-sm w-full"
                      value={p.type}
                      onChange={(e) => updateAt(form, set, "promoCodes", i, { type: e.target.value })}
                      aria-label="Discount type"
                    >
                      <option value="flat">RM off</option>
                      <option value="percent">% off</option>
                    </select>
                  </RowField>
                  <RowField label="Amount" className={HALF_ON_MOBILE + " sm:w-24"}>
                    <input
                      className="input input-bordered input-sm w-full"
                      inputMode="decimal"
                      value={p.value}
                      onChange={(e) => updateAt(form, set, "promoCodes", i, { value: e.target.value })}
                      placeholder={p.type === "percent" ? "10" : "500.00"}
                      aria-label="Discount amount"
                    />
                  </RowField>
                  <RowField label="Expires" className="w-full sm:w-auto">
                    <input
                      type="date"
                      className="input input-bordered input-sm w-full"
                      value={p.expires}
                      onChange={(e) => updateAt(form, set, "promoCodes", i, { expires: e.target.value })}
                      aria-label="Expiry date"
                    />
                  </RowField>
                  <button
                    type="button"
                    className="hidden sm:inline-flex btn btn-xs btn-ghost"
                    onClick={() =>
                      set({ promoCodes: form.promoCodes.filter((_, j) => j !== i) })
                    }
                    aria-label="Remove code"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
          <button
            className="btn btn-sm btn-ghost self-start mt-2"
            onClick={() =>
              set({
                promoCodes: [
                  ...form.promoCodes,
                  { code: "", type: "flat", value: "", expires: "" },
                ],
              })
            }
          >
            + Add code
          </button>
          <p className="text-xs opacity-60 mt-1">
            Redemption limits aren’t enforced yet — a code stays valid until its
            expiry date.
          </p>
        </div>

        {/* ---- Seats ---- */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="form-control">
            <label className="label">
              <span className="label-text">Seats counter</span>
              <span className="label-text-alt opacity-60">blank = hidden</span>
            </label>
            <input
              className="input input-bordered"
              inputMode="numeric"
              value={form.seatsStart}
              onChange={(e) => set({ seatsStart: e.target.value })}
              placeholder="25"
            />
          </div>
          <div className="form-control">
            <label className="label">
              <span className="label-text">Never drops below</span>
            </label>
            <input
              className="input input-bordered"
              inputMode="numeric"
              value={form.seatsFloor}
              onChange={(e) => set({ seatsFloor: e.target.value })}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

/* How the stack will actually look once published. The gradient and colours are
   copied from .bonus-row in public/checkout/styles.css — this is a preview, not
   the live markup, so if you change one, change the other. Worth the small
   duplication: the team edits these minutes before a webinar closes, and
   "publish and go look" is a two-minute round trip. */
const GOLD =
  "linear-gradient(112deg,#A9822A 0%,#D9B24C 12%,#F6E19B 26%,#FFF8DC 38%," +
  "#EBCB6B 50%,#C9A02F 64%,#E9CE79 78%,#FFF3BF 90%,#C79E2C 100%)";

/* Both languages of every fixed string the bake script writes, kept here in the
   same shape so the two can be diffed by eye. Admin-entered copy falls back to
   English when its 中文 twin is blank, matching bilingual() in the bake. */
const PREVIEW_COPY = {
  en: {
    head: "Included when you complete today",
    label: (n) => `Bonus ${n}:`,
    totalLead: "Total bonus value",
    totalTail: "— included at no extra cost",
  },
  zh: {
    head: "今天完成付款即可获得",
    label: (n) => `赠品 ${n}：`,
    totalLead: "赠品总价值",
    totalTail: "— 免费赠送",
  },
};

function BonusPreview({ bonuses }) {
  // The page ships both languages and the buyer picks; previewing only English
  // hides exactly the mistakes the 中文 fields are there to catch — a missing
  // translation, or one long enough to break the row.
  const [lang, setLang] = useState("en");
  const t = PREVIEW_COPY[lang];
  const rows = bonuses.filter((b) => b.name.trim());
  const total = totalBonusValue(rows);

  return (
    <div className="mt-3">
      <div className="flex items-center gap-2 mb-1">
        <div className="text-xs opacity-60">Preview</div>
        <div role="tablist" className="tabs tabs-boxed tabs-xs">
          {[
            ["en", "EN"],
            ["zh", "中文"],
          ].map(([code, label]) => (
            <button
              key={code}
              role="tab"
              type="button"
              className={`tab ${lang === code ? "tab-active" : ""}`}
              aria-selected={lang === code}
              onClick={() => setLang(code)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>
      <div
        className="rounded-2xl p-3"
        style={{ background: "#142845", border: "2px solid #FFD95A" }}
      >
        <div
          className="flex items-center gap-2 mb-2 text-sm font-bold"
          style={{ color: "#FFD95A" }}
        >
          🎁 {t.head}
        </div>
        {rows.map((b, i) => {
          // Mirrors .bonus-row.is-struck on the live page: dimmed, drained of
          // the gold, struck end to end. Same reason as the gradient above —
          // seeing it here beats publishing to find out.
          const struck = isBonusStruck(b);
          const strike = struck ? "line-through" : "none";
          return (
            <div
              key={b._id || i}
              className="flex items-center gap-1.5 rounded-lg mb-1.5 px-2 py-2 text-[11px]"
              style={{
                background: GOLD,
                color: "#2A1D00",
                opacity: struck ? 0.45 : 1,
                filter: struck ? "saturate(.25)" : "none",
              }}
            >
              {b.icon && (
                <span style={{ color: "#5A4000", display: "inline-flex" }}>
                  <BonusIcon name={b.icon} size={16} />
                </span>
              )}
              <span
                className="font-extrabold"
                style={{ color: "#5A4000", textDecoration: strike }}
              >
                {t.label(i + 1)}
              </span>
              <span
                className="flex-1 min-w-0 truncate font-semibold"
                style={{ color: "#241900", textDecoration: strike }}
              >
                {(lang === "zh" && bonusTitleText(b.nameZh)) || bonusTitleText(b.name)}
              </span>
              {b.tag && (
                <span
                  className="px-1 py-0.5 rounded text-[8px] font-extrabold whitespace-nowrap"
                  style={{ background: struck ? "#6B6257" : "#C20A17", color: "#fff" }}
                >
                  {(lang === "zh" && b.tagZh) || b.tag}
                </span>
              )}
              {(() => {
                // A figure is language-neutral and ships as one span; a label is
                // admin copy and follows the switch, falling back to English.
                const parsed = parseBonusValue(b.value);
                if (parsed.kind === "none") return null;
                return (
                  <span
                    className="font-extrabold"
                    style={{ color: "#1E1400", textDecoration: strike }}
                  >
                    {parsed.kind === "number"
                      ? formatBonusMoney(parsed.value)
                      : (lang === "zh" && b.valueZh) || parsed.value}
                  </span>
                );
              })()}
            </div>
          );
        })}
        {total > 0 && (
          <p
            className="mt-2 pt-2 text-center text-[11px]"
            style={{
              borderTop: "1px solid rgba(255,217,90,.3)",
              color: "rgba(255,255,255,.85)",
            }}
          >
            {t.totalLead}{" "}
            <b style={{ color: "#FFD95A" }}>{formatBonusMoney(total)}</b>{" "}
            {t.totalTail}
          </p>
        )}
      </div>
    </div>
  );
}

function updateAt(form, set, key, index, patch) {
  set({
    [key]: form[key].map((item, i) => (i === index ? { ...item, ...patch } : item)),
  });
}

